// Asbestos Management — top-level register (Safety & Compliance). Lists buildings
// that already have asbestos data; a row opens the building-scoped workspace.
// "Add building" lets internal staff start asbestos management for a new building.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Biohazard, Plus, Building as BuildingIcon, Layers, Search, X, ArrowUpRight, List } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import AsbestosRecordsBrowser from "./AsbestosRecordsBrowser";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);
const ACCENT = "#C8632A";

export default function AsbestosManagementPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [tab, setTab] = useState("buildings");   // buildings | records
    const canEdit = user && CAN_EDIT_ROLES.has(user.role) && isInternalView;

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (portalClientId && !isInternalView) params.client_id = portalClientId;
        api.get("/asbestos/register", { params })
            .then((r) => setRows(r.data || []))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load the asbestos register"))
            .finally(() => setLoading(false));
    }, [portalClientId, isInternalView]);
    useEffect(load, [load]);

    const openBuilding = (bid) => navigate(`/app/buildings/${bid}/asbestos`);
    const fmt = (s) => { try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }); } catch { return "—"; } };

    return (
        <div className="space-y-6" data-testid="asbestos-management-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <span className="inline-flex items-center gap-1.5 pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: `2px solid ${ACCENT}`, color: "#6F6A64" }}>
                        <Biohazard className="w-3.5 h-3.5" /> Safety &amp; Compliance
                    </span>
                    <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#1F1F1F]">Asbestos Management</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        Standardised floorplans with mapped asbestos zones · click a building to open its register
                    </p>
                </div>
                {canEdit && tab === "buildings" && (
                    <button data-testid="asbestos-add-building" onClick={() => setAdding(true)}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-[10px] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
                        style={{ background: ACCENT }}>
                        <Plus className="w-4 h-4" /> Add building
                    </button>
                )}
            </div>

            <div className="inline-flex rounded-[10px] border bg-white p-0.5" style={{ borderColor: "#E5E0DA" }}>
                <button data-testid="asbestos-tab-buildings" onClick={() => setTab("buildings")}
                    className={`h-8 px-3.5 rounded-[8px] text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${tab === "buildings" ? "text-white" : ""}`}
                    style={tab === "buildings" ? { background: ACCENT } : { color: "#6F6A64" }}>
                    <BuildingIcon className="w-3.5 h-3.5" /> By building
                </button>
                <button data-testid="asbestos-tab-records" onClick={() => setTab("records")}
                    className={`h-8 px-3.5 rounded-[8px] text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${tab === "records" ? "text-white" : ""}`}
                    style={tab === "records" ? { background: ACCENT } : { color: "#6F6A64" }}>
                    <List className="w-3.5 h-3.5" /> All records
                </button>
            </div>

            {tab === "records" ? <AsbestosRecordsBrowser /> : loading ? (
                <div className="font-serif-italic text-[#9A968F] py-12 text-center">Loading register…</div>
            ) : rows.length === 0 ? (
                <div data-testid="asbestos-empty" className="bg-white border rounded-[13px] py-16 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <Biohazard className="w-9 h-9 mx-auto mb-3" style={{ color: "#D8CFC4" }} />
                    <p className="text-[14px] font-semibold text-[#1F1F1F]">No asbestos records yet</p>
                    <p className="text-[12.5px] mt-1 mb-4" style={{ color: "#6F6A64" }}>
                        {canEdit ? "Add a building to upload its floorplans and map asbestos zones." : "No buildings have asbestos data in your portal yet."}
                    </p>
                    {canEdit && (
                        <button data-testid="asbestos-empty-add" onClick={() => setAdding(true)}
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-[10px] text-white text-[13px] font-semibold" style={{ background: ACCENT }}>
                            <Plus className="w-4 h-4" /> Add building
                        </button>
                    )}
                </div>
            ) : (
                <div className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[12.7px]" data-testid="asbestos-register-table">
                            <thead>
                                <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                                    <th className="text-left px-5 py-2.5 font-bold">Building</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Client</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Site</th>
                                    <th className="text-center px-3 py-2.5 font-bold">Floors</th>
                                    <th className="text-center px-3 py-2.5 font-bold">Present</th>
                                    <th className="text-center px-3 py-2.5 font-bold">None</th>
                                    <th className="text-center px-3 py-2.5 font-bold">Unknown</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Status</th>
                                    <th className="text-left px-3 py-2.5 font-bold">Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.building_id} data-testid={`asbestos-row-${r.building_id}`}
                                        onClick={() => openBuilding(r.building_id)}
                                        className="border-t hover:bg-[#FCFBF9] transition-colors cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-2 font-semibold text-[#1F1F1F]">
                                                <BuildingIcon className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                                                {r.building_name}
                                                <ArrowUpRight className="w-3 h-3 text-[#C7C2BB]" />
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{r.client_name || "—"}</td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{r.site_name || "—"}</td>
                                        <td className="px-3 py-3 text-center text-[#2D2D2D]">{r.floors}</td>
                                        <td className="px-3 py-3 text-center font-semibold" style={{ color: r.zones_red ? "#B33A2E" : "#9A968F" }}>{r.zones_red}</td>
                                        <td className="px-3 py-3 text-center font-semibold" style={{ color: r.zones_green ? "#2E7D4F" : "#9A968F" }}>{r.zones_green}</td>
                                        <td className="px-3 py-3 text-center text-[#6F6A64]">{r.zones_unknown}</td>
                                        <td className="px-3 py-3">
                                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{r.status}</span>
                                        </td>
                                        <td className="px-3 py-3 text-[#6F6A64]">{fmt(r.updated_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {adding && <AddBuildingDialog existingIds={new Set(rows.map((r) => r.building_id))}
                onClose={() => setAdding(false)} onPick={(bid) => openBuilding(bid)} />}
        </div>
    );
}

function AddBuildingDialog({ existingIds, onClose, onPick }) {
    const [buildings, setBuildings] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [creating, setCreating] = useState(false);
    const [newClient, setNewClient] = useState(false);
    const [clientId, setClientId] = useState("");
    const [clientName, setClientName] = useState("");
    const [buildingName, setBuildingName] = useState("");
    const [buildingType, setBuildingType] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([api.get("/buildings"), api.get("/clients")])
            .then(([b, c]) => { setBuildings(b.data || []); setClients(c.data || []); })
            .catch(() => { setBuildings([]); setClients([]); })
            .finally(() => setLoading(false));
    }, []);
    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return buildings.filter((b) => !existingIds.has(b.id) && (!term || (b.name || "").toLowerCase().includes(term)));
    }, [buildings, existingIds, q]);

    async function createAndOpen() {
        if (!buildingName.trim()) { toast.error("Building name is required"); return; }
        if (newClient ? !clientName.trim() : !clientId) { toast.error(newClient ? "Client name is required" : "Select a client"); return; }
        setSaving(true);
        try {
            let cid = clientId;
            if (newClient) {
                const { data: c } = await api.post("/clients", { name: clientName.trim() });
                cid = c.id;
            }
            const { data: bld } = await api.post("/buildings", {
                client_id: cid, name: buildingName.trim(), building_type: buildingType.trim() || null,
            });
            toast.success(`${bld.name} created`);
            onPick(bld.id);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not create the building");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4" style={{ background: "rgba(20,18,16,0.45)" }} onClick={onClose}>
            <div data-testid="asbestos-add-dialog" className="bg-white rounded-[14px] w-full max-w-md shadow-brand-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F] flex items-center gap-2"><Layers className="w-4 h-4" style={{ color: ACCENT }} /> {creating ? "Create a new building" : "Add a building"}</h3>
                    <button data-testid="asbestos-add-close" onClick={onClose} className="text-[#9A968F] hover:text-[#1F1F1F]"><X className="w-4 h-4" /></button>
                </div>

                {!creating ? (
                    <div className="p-4">
                        <div className="relative mb-3">
                            <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#9A968F]" />
                            <input data-testid="asbestos-add-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buildings…"
                                className="w-full h-9 pl-9 pr-3 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </div>
                        <div className="max-h-[40vh] overflow-y-auto space-y-1">
                            {loading ? <p className="text-[12.5px] text-[#9A968F] text-center py-6">Loading buildings…</p>
                                : filtered.length === 0 ? <p className="text-[12.5px] text-[#9A968F] text-center py-6">No buildings match — create a new one below.</p>
                                    : filtered.map((b) => (
                                        <button key={b.id} data-testid={`asbestos-pick-${b.id}`} onClick={() => onPick(b.id)}
                                            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[9px] hover:bg-[#F6F4F0] text-left transition-colors">
                                            <BuildingIcon className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                                            <span className="flex-1">
                                                <span className="block text-[13px] font-semibold text-[#1F1F1F]">{b.name}</span>
                                                {b.building_type && <span className="block text-[11px] text-[#9A968F]">{b.building_type}</span>}
                                            </span>
                                            <ArrowUpRight className="w-3.5 h-3.5 text-[#C7C2BB]" />
                                        </button>
                                    ))}
                        </div>
                        <button data-testid="asbestos-create-new-toggle" onClick={() => setCreating(true)}
                            className="w-full mt-3 h-9 rounded-[9px] border-2 border-dashed text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5" style={{ borderColor: "#E2DDD5", color: ACCENT }}>
                            <Plus className="w-3.5 h-3.5" /> Can't find it? Create a new building
                        </button>
                    </div>
                ) : (
                    <div className="p-4 space-y-3">
                        <div>
                            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9A968F" }}>Client</div>
                            {!newClient ? (
                                <select data-testid="asbestos-new-building-client" value={clientId} onChange={(e) => setClientId(e.target.value)}
                                    className="w-full h-9 px-2.5 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                                    <option value="">Select a client…</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            ) : (
                                <input data-testid="asbestos-new-client-name" autoFocus value={clientName} onChange={(e) => setClientName(e.target.value)}
                                    placeholder="New client name" className="w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                            )}
                            <button data-testid="asbestos-new-client-toggle" onClick={() => setNewClient((v) => !v)}
                                className="text-[11px] mt-1 font-semibold" style={{ color: ACCENT }}>
                                {newClient ? "← Choose an existing client instead" : "+ This is a new client"}
                            </button>
                        </div>
                        <div>
                            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9A968F" }}>Building name</div>
                            <input data-testid="asbestos-new-building-name" value={buildingName} onChange={(e) => setBuildingName(e.target.value)}
                                placeholder="e.g. Beacon Tower" className="w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </div>
                        <div>
                            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9A968F" }}>Building type (optional)</div>
                            <input data-testid="asbestos-new-building-type" value={buildingType} onChange={(e) => setBuildingType(e.target.value)}
                                placeholder="e.g. High-rise residential" className="w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </div>
                        <div className="flex gap-2 pt-1">
                            <button data-testid="asbestos-create-back" onClick={() => setCreating(false)}
                                className="flex-1 h-9 rounded-lg border text-[12.5px] font-semibold" style={{ borderColor: "#E2DDD5", color: "#6F6A64" }}>Back</button>
                            <button data-testid="asbestos-create-submit" disabled={saving} onClick={createAndOpen}
                                className="flex-1 h-9 rounded-lg text-white text-[12.5px] font-semibold disabled:opacity-60" style={{ background: ACCENT }}>
                                {saving ? "Creating…" : "Create & open"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
